import React from 'react';
import { useLang } from '../i18n/useLang';

const packageConfig = [
	{
		key: 'sprint',
		icon: '🚀',
		accentCls: 'text-nf-teal',
		topFrom: '#14b8a6',
		topTo: '#06b6d4',
		popular: false,
	},
	{
		key: 'engage',
		icon: '🤝',
		accentCls: 'text-nf-p3',
		topFrom: '#6d28d9',
		topTo: '#a78bfa',
		popular: true,
	},
	{
		key: 'consult',
		icon: '💎',
		accentCls: 'text-nf-amber',
		topFrom: '#f59e0b',
		topTo: '#fde047',
		popular: false,
	},
];

// [sprint, engage, consult] — 'check' | 'cross' | custom text
const TABLE_VALS = [
	['check', 'check', 'cross'],   // AI prototype delivery
	['check', 'check', 'check'],   // Domain expert team
	['14 days', 'Ongoing', '—'],  // Post-release support
	['cross', 'check', 'cross'],   // Long-term engagement
	['cross', 'check', 'cross'],   // Team training & handover
	['cross', 'check', 'cross'],   // Dedicated account manager
	['cross', 'cross', 'check'],   // Existing codebase review
	['cross', 'check', 'check'],   // Architecture advisory
	['check', 'check', 'check'],   // Risk & impact assessment
	['cross', 'check', 'check'],   // Written expert report
];

function Cell({ val, popular }) {
	if (val === 'check') return (
		<span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-nf-green/15 text-nf-green text-[13px] font-bold">✓</span>
	);
	if (val === 'cross') return (
		<span className="inline-flex items-center justify-center w-6 h-6 text-nf-t3 text-base">—</span>
	);
	return <span className={`text-[12px] font-semibold ${popular ? 'text-nf-p3' : 'text-nf-t2'}`}>{val}</span>;
}

export default function Offers({ navigate }) {
	const { t } = useLang();
	const pkgs = packageConfig.map((cfg, i) => ({ ...cfg, ...t('offers.packages')[i] }));
	const features = t('offers.tableFeatures');

	return (
		<div>
			{/* Hero */}
			<div className="pt-[72px] sm:pt-[80px] pb-8 lg:pb-12 px-4 sm:px-8 lg:px-20 text-center relative overflow-hidden">
				<div className="absolute inset-0 pointer-events-none"
					style={{ background: 'radial-gradient(ellipse 50% 60% at 50% 30%, rgba(109,40,217,.12) 0%, transparent 65%)' }} />
				<div className="relative max-w-[1100px] mx-auto">
					<span className="sec-label">{t('offers.secLabel')}</span>
					<h1 className="font-heading font-black leading-[1.05] tracking-[-0.03em] max-w-[700px] mx-auto mt-4 mb-4"
						style={{ fontSize: 'clamp(26px, 3.8vw, 52px)' }}>
						{t('offers.h1')[0]}<br /><span className="text-nf-p3">{t('offers.h1')[1]}</span>
					</h1>
					<p className="text-[15px] text-nf-t2 max-w-[580px] mx-auto">{t('offers.heroBody')}</p>
				</div>
			</div>

			{/* Pricing cards */}
			<section id="offers-packages" className="px-4 sm:px-8 lg:px-20 pb-12 lg:pb-16">
				<div className="max-w-[960px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
					{pkgs.map((p) => (
						<div
							key={p.key}
							className={`h-full relative flex flex-col bg-nf-bg3 border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${p.popular
								? 'border-[rgba(109,40,217,0.5)] shadow-[0_0_40px_rgba(109,40,217,0.18)] lg:scale-[1.03]'
								: 'border-white/[0.06]'
								}`}
						>
							{/* Top gradient bar */}
							<div className="h-1 w-full shrink-0" style={{ background: `linear-gradient(90deg, ${p.topFrom}, ${p.topTo})` }} />

							{/* Popular badge */}
							{p.popular && (
								<div className="absolute top-5 right-5 text-[11px] font-bold px-2.5 py-1 rounded-full"
									style={{ background: 'rgba(109,40,217,0.2)', color: '#a78bfa', border: '1px solid rgba(109,40,217,0.3)' }}>
									{p.badge}
								</div>
							)}

							<div className="p-7 flex flex-col flex-1">
								<div className="text-[32px] mb-3">{p.icon}</div>
								{!p.popular && (
									<div className={`text-[11px] font-bold uppercase tracking-[0.08em] mb-2 ${p.accentCls}`}>{p.badge}</div>
								)}
								<h2 className="font-heading text-[22px] font-black mb-1">{p.name}</h2>
								<p className="text-[13px] text-nf-t2 mb-5 leading-[1.65]">{p.tagline}</p>

								<div className="mb-5 pb-5 border-b border-white/[0.06]">
									<div className={`font-heading text-[34px] font-black tracking-[-0.03em] ${p.accentCls}`}>{p.price}</div>
									<div className="text-[12px] text-nf-t3 mt-0.5">{p.priceSub}</div>
								</div>

								<p className="text-[13px] text-nf-t2 leading-[1.7] mb-6">{p.desc}</p>

								<ul className="flex flex-col gap-2.5 mb-8">
									{p.highlights.map((h, j) => (
										<li key={j} className="flex items-start gap-2.5 text-[13px] text-nf-t2">
											<span className={`shrink-0 font-bold mt-[1px] ${p.accentCls}`}>✓</span>
											{h}
										</li>
									))}
								</ul>

								<a
									className={`mt-auto block text-center font-semibold text-[15px] py-3 rounded-xl transition-all cursor-pointer no-underline ${p.popular ? 'btn-cta' : 'btn-outline'}`}
									href="https://cal.com/willy-carlos-tchuitcheu-lz7ww7/15min?overlayCalendar=true"
									target="_blank"
									rel="noopener noreferrer">
									{p.cta}
								</a>
							</div>
						</div>
					))}
				</div>
			</section>

			{/* Comparison table */}
			<section id="offers-compare" className="py-16 lg:py-20 px-4 sm:px-8 lg:px-20 bg-nf-bg2">
				<div className="max-w-[960px] mx-auto">
					<h2 className="font-heading text-[28px] lg:text-[36px] font-black mb-2 text-center">{t('offers.compareTitle')}</h2>
					<p className="text-[15px] text-nf-t2 text-center mb-10">{t('offers.compareSub')}</p>

					<div className="overflow-x-auto rounded-2xl border border-white/[0.06]">
						<table className="w-full border-collapse min-w-[480px]">
							<thead>
								<tr className="bg-nf-bg3 border-b border-white/[0.06]">
									<th className="text-left px-6 py-4 text-[12px] font-semibold text-nf-t3 uppercase tracking-[0.06em] w-[40%]">{t('offers.tableFeatureCol')}</th>
									{pkgs.map((p) => (
										<th key={p.key} className={`px-4 py-4 text-center text-[13px] font-extrabold ${p.accentCls}`}>
											{p.name}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{features.map((feat, ri) => (
									<tr key={ri} className={`border-b border-white/[0.04] last:border-0 ${ri % 2 !== 0 ? 'bg-nf-bg3/30' : ''}`}>
										<td className="px-6 py-4 text-[13px] text-nf-t2">{feat}</td>
										{TABLE_VALS[ri].map((val, ci) => (
											<td key={ci} className={`px-4 py-4 text-center ${pkgs[ci].popular ? 'bg-[rgba(109,40,217,0.04)]' : ''}`}>
												<Cell val={val} popular={pkgs[ci].popular} />
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</section>

			{/* CTA */}
			<div className="py-16 lg:py-24 px-4 sm:px-8 lg:px-20 text-center relative overflow-hidden">
				<div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_65%_at_50%_50%,rgba(109,40,217,0.12),transparent_70%)] pointer-events-none" />
				<div className="relative max-w-[600px] mx-auto">
					<h2 className="font-heading text-[28px] lg:text-[40px] font-black mb-4">{t('offers.ctaTitle')}</h2>
					<p className="text-[16px] text-nf-t2 mb-8 leading-[1.7]">{t('offers.ctaBody')}</p>
					<a href="https://cal.com/willy-carlos-tchuitcheu-lz7ww7/15min?overlayCalendar=true" target="_blank" rel="noopener noreferrer" className="btn-cta btn-lg cursor-pointer no-underline">{t('offers.ctaBtn')}</a>
				</div>
			</div>
		</div>
	);
}
